import { useEffect, useState } from "react";
import { Shield, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS, COMPANY } from "../../lib/site-content";
import { scrollToId } from "../../lib/scroll";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200 shadow-[0_8px_30px_rgba(15,23,42,0.05)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[74px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="logo-home"
          onClick={() => go("#home")}
          className="group flex items-center gap-2.5"
        >
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#0F172A] text-white transition-transform duration-300 group-hover:-rotate-6">
            <Shield className="h-5 w-5" strokeWidth={2.2} />
          </span>
          <span className="font-display text-lg font-extrabold tracking-tight text-[#0F172A]">
            {COMPANY.name}
          </span>
        </button>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="relative rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:text-[#0F172A]"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="header-quickscan-cta"
            onClick={() => go("#contact")}
            className="hidden rounded-full bg-[#F59E0B] px-5 py-2.5 text-sm font-bold text-[#0F172A] shadow-[0_8px_20px_rgba(245,158,11,0.35)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#f0a92a] sm:inline-flex"
          >
            Gratis Quickscan
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 bg-white text-[#0F172A] lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="rounded-xl px-4 py-3 text-left text-base font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-2 rounded-full bg-[#F59E0B] px-5 py-3 text-center text-sm font-bold text-[#0F172A]"
              >
                Gratis Quickscan
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
